import type { IAgentToolContext, IAgentTool } from '../../../../core/ai/agent/IAgentTool';
import { resolveNodeByName } from '../../../../core/ai/agent/utils/resolveNodeByName';
import { useDiagramStore } from '../../../../stores/diagramStore';

export const highlightPathTool: IAgentTool = {
  name: 'highlight_path',
  description: 'Highlight a specific path between two nodes in the flowchart.',
  parameters: {
    type: 'object',
    properties: {
      from: { type: 'string', description: 'Starting node name' },
      to: { type: 'string', description: 'Ending node name' },
    },
    required: ['from', 'to'],
  },
  supportedDiagramTypes: ['flowchart'],
  tier: 'visual',
  category: 'display',

  async execute(params: Record<string, unknown>, context: IAgentToolContext) {
    const fromName = String(params.from ?? '').trim();
    const toName = String(params.to ?? '').trim();
    if (!fromName || !toName) return { success: false, summary: 'Missing start or end node.' };

    const fromNode = resolveNodeByName(fromName, context.diagramData.nodes);
    const toNode = resolveNodeByName(toName, context.diagramData.nodes);

    if (fromNode.status !== 'found' || toNode.status !== 'found') {
      return { success: false, summary: 'Could not resolve start or end node.' };
    }

    const adjacency = new Map<string, string[]>();
    context.diagramData.edges.forEach((edge) => {
      const list = adjacency.get(edge.from) ?? [];
      list.push(edge.to);
      adjacency.set(edge.from, list);
    });

    const queue: Array<{ id: string; path: string[] }> = [{ id: fromNode.node.id, path: [fromNode.node.id] }];
    const visited = new Set<string>();
    let path: string[] | null = null;

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) break;
      if (current.id === toNode.node.id) {
        path = current.path;
        break;
      }
      if (visited.has(current.id)) continue;
      visited.add(current.id);
      (adjacency.get(current.id) ?? []).forEach((next) => {
        queue.push({ id: next, path: [...current.path, next] });
      });
    }

    if (!path) {
      return { success: false, summary: `No path found from ${fromName} to ${toName}.` };
    }

    if (context.stores.highlight?.setHighlightedIds) {
      context.stores.highlight.setHighlightedIds(new Set(path));
    }
    // Selection renders for every node type (the highlight store does not on
    // process canvases), so also select the path to give a visible result.
    useDiagramStore.getState().setSelected(new Set(path));

    return {
      success: true,
      summary: `Highlighted path from ${fromName} to ${toName}.`,
      data: { nodeIds: path },
    };
  },
};
